import * as Yup from 'yup';
import Inputmask from 'inputmask';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Unstable_Grid2';
import LoadingButton from '@mui/lab/LoadingButton';
import { Typography, InputAdornment } from '@mui/material';

import { updateCheck, getBankAccounts } from 'src/services/finance/finance-service-calls';

import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';

import RHFDatePicker from './RHFDatepicker';

export default function CheckNewEditForm({ currentRequest, openCloseModal, refreshData }) {
  const { enqueueSnackbar } = useSnackbar();
  const [accounts, setAccounts] = useState([]);
  const [, setLoadingAccounts] = useState(true);

  const EditStateSchema = Yup.object().shape({
    checkNumber: Yup.string().required('Se necesita el número de cheque'),
    bankAccount: Yup.string().required('Se necesita una cuenta de banco'),
    amount: Yup.number().required('Se necesita ingresar un monto'),
    Description: Yup.string(),
    CheckDate: Yup.date().nullable(),
    CreationDate: Yup.date().required('Se necesita una fecha de creación'),
    nameRecieved: Yup.string().optional(),
    IdentificationRecieved: Yup.string().optional(),
    RecievedDate: Yup.date().nullable(),
  });

  const defaultValues = useMemo(
    () => ({
      checkNumber: currentRequest?.checkNumber || '',
      bankAccount:
        currentRequest?.bankAccount?.bankAccountId || currentRequest?.bankAccount?.name || '',
      amount: currentRequest?.amount || 0,
      Description: currentRequest?.description || '',
      CheckDate: currentRequest?.checkDate ? new Date(currentRequest.checkDate) : null,
      CreationDate: currentRequest?.creationDate
        ? new Date(currentRequest.creationDate)
        : new Date(),
      nameRecieved: currentRequest?.recievedName?.name || '',
      IdentificationRecieved: currentRequest?.recievedName?.identification || '',
      RecievedDate: currentRequest?.receivedDate ? new Date(currentRequest.receivedDate) : null,
    }),
    [currentRequest]
  );

  const methods = useForm({
    resolver: yupResolver(EditStateSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const payload = {
        checkNumber: data.checkNumber || currentRequest?.checkNumber || '',
        bankAccountId:
          data.bankAccount ||
          currentRequest?.bankAccount?.bankAccountId ||
          currentRequest?.bankAccount?.id ||
          '',
        amount: parseFloat(data.amount) || currentRequest?.amount || 0,
        status: currentRequest?.isDeleted === 1 ? 1 : 0,
        description: data.Description || currentRequest?.description || '',
        templateId: currentRequest?.templateId || crypto.randomUUID(),
        recievedName:
          data.nameRecieved ||
          currentRequest?.recievedName?.name ||
          currentRequest?.recievedName ||
          '',
        checkDate: data.CheckDate
          ? new Date(data.CheckDate).toISOString()
          : currentRequest?.checkDate || new Date().toISOString(),
        createUser: currentRequest?.createUser || currentRequest?.createdBy?.userId || '',
        accountCatalog: currentRequest?.accountCatalog || data.bankAccount || '',
        procedentType: currentRequest?.procedentType || 0,
        otherAffectedAccount: currentRequest?.otherAffectedAccount || crypto.randomUUID(),
      };

      const response = await updateCheck(currentRequest.checkId, payload);

      if (response.status === 200) {
        enqueueSnackbar('Cheque actualizado exitosamente!', { variant: 'success' });
        reset();
        openCloseModal?.onFalse();
        refreshData?.();
      } else {
        enqueueSnackbar('Error al actualizar el cheque', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Error de conexión al actualizar el cheque', { variant: 'error' });
    }
  });

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoadingAccounts(true);
        const response = await getBankAccounts();

        if (response.status === 200) {
          setAccounts(response.data);
        } else {
          enqueueSnackbar('Error al cargar las cuentas bancarias', { variant: 'error' });
        }
      } catch (error) {
        enqueueSnackbar('Error de conexión al cargar las cuentas bancarias', { variant: 'error' });
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchAccounts();
  }, [enqueueSnackbar]);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid xs={12} md={8} justifyContent="center" mb={3}>
        <Stack mb={6} mt={1} spacing={3}>
          <RHFTextField name="checkNumber" label="Número de cheque" />
          <Stack display="flex" flexDirection="row" columnGap={2}>
            <RHFSelect name="bankAccount" label="Cuenta de banco">
              {accounts.map((option) => (
                <MenuItem key={option.bankAccountId} value={option.bankAccountId}>
                  {option.account}
                </MenuItem>
              ))}
            </RHFSelect>
            <RHFTextField
              name="amount"
              label="Monto"
              inputProps={{
                ref: (input) => {
                  if (input && !input.dataset.masked) {
                    Inputmask({
                      alias: 'numeric',
                      groupSeparator: ',',
                      autoGroup: true,
                      digits: 2,
                      digitsOptional: false,
                      placeholder: '0',
                      rightAlign: false,
                      autoUnmask: true,
                      removeMaskOnSubmit: true,
                      clearMaskOnLostFocus: false,
                    }).mask(input);
                    input.dataset.masked = 'true';
                    setTimeout(() => {
                      const length = input.value?.length;
                      input.setSelectionRange(length, length);
                    }, 0);
                  }
                },
              }}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ typography: 'subtitle2', color: 'text.disabled' }}>$</Box>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <RHFTextField name="Description" label="Descripción" />
          <Stack display="flex" flexDirection="row" columnGap={2}>
            <RHFDatePicker name="CheckDate" label="Fecha de ejecución" />
            <RHFDatePicker name="CreationDate" label="Fecha de creación" />
          </Stack>
          {!(
            new Date(currentRequest?.receivedDate) > new Date() ||
            currentRequest?.receivedDate === null ||
            currentRequest?.receivedDate === undefined
          ) &&
            currentRequest?.isDeleted !== 1 && (
              <>
                <Typography typography="subtitle1" sx={{ pl: 1 }}>
                  Recibido
                </Typography>
                <RHFTextField name="nameRecieved" label="¿Quién lo recibió?" />
                <Stack display="flex" columnGap={2} flexDirection="row">
                  <RHFTextField
                    name="IdentificationRecieved"
                    label="Identificación"
                    inputProps={{
                      ref: (input) => {
                        if (input && !input.dataset.masked) {
                          Inputmask({
                            mask: '999-9999999-9',
                            placeholder: '_',
                            clearMaskOnLostFocus: false,
                            removeMaskOnSubmit: true,
                            autoUnmask: true,
                          }).mask(input);
                          input.dataset.masked = 'true';

                          setTimeout(() => {
                            const length = input.value?.length || 0;
                            input.setSelectionRange(length, length);
                          }, 0);
                        }
                      },
                    }}
                  />
                  <RHFDatePicker name="RecievedDate" label="¿Cuándo se recibió?" />
                </Stack>
              </>
            )}
        </Stack>
        <Stack justifyContent="flex-end" direction="row" sx={{ mt: 3, mb: 3 }} spacing={2}>
          <Button
            onClick={() => {
              openCloseModal?.onFalse();
              refreshData();
            }}
            variant="contained"
            color="error"
            sx={{ width: '30%' }}
          >
            Cancelar
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            color="success"
            fullWidth
          >
            Guardar Cambios
          </LoadingButton>
        </Stack>
      </Grid>
    </FormProvider>
  );
}

CheckNewEditForm.propTypes = {
  currentRequest: PropTypes.object,
  openCloseModal: PropTypes.object,
  refreshData: PropTypes.func,
};
